import { useCallback, useRef } from 'react'
import { observer } from 'mobx-react'
import { matchPath, useLocation } from 'react-router-dom'
import { ZaiChatAccessProvider } from '@winnin/zai-chat'

import { useZaiBackgroundNotification } from 'modules/zai/zai-background-notification'
import FeatureFlagsStore from 'stores/feature-flags'
import { PATHNAMES } from 'utils/routes/consts'

import type {
  TZaiChatAccessProviderMiddlewareProps,
  TZaiSwitchToThread,
} from './zai-chat-access-provider-middleware.types'

/**
 * observer: the Connections-panel flag is a ConfigCat flag evaluated per user × workspace
 * (identifier + custom.workspaceId), so the panel appears only where it is targeted on.
 */
export const ZaiChatAccessProviderMiddleware = observer(
  ({ children, ...accessProps }: TZaiChatAccessProviderMiddlewareProps) => {
    const { pathname } = useLocation()
    const switchToThreadRef = useRef<TZaiSwitchToThread>()

    const setAssistantUiControl = useCallback((switchToThread: TZaiSwitchToThread) => {
      switchToThreadRef.current = switchToThread
    }, [])

    const isChatVisible = useCallback(() => Boolean(matchPath(pathname, { path: PATHNAMES.WORKSPACE_ZAI })), [pathname])

    const { notifyBackgroundThreadComplete } = useZaiBackgroundNotification(switchToThreadRef, isChatVisible)

    return (
      <ZaiChatAccessProvider
        {...accessProps}
        onBackgroundThreadComplete={notifyBackgroundThreadComplete}
        setAssistantUiControl={setAssistantUiControl}
        isChatVisible={isChatVisible}
        isConnectionsEnabled={FeatureFlagsStore.getFeatureFlag('isExternalMcpConnectionsEnabled')}
      >
        {children}
      </ZaiChatAccessProvider>
    )
  }
)
